import './auth.css'
import { reportPageView } from './pageview.js'
import { fetchCurrentUser, resolveNextPath } from './auth-state.js'
import { getLocale, localeHeaderValue, onLocaleChange } from './locale.js'
import { getMessages } from './i18n.js'

const mode = document.body.dataset.authMode
const form = document.getElementById('auth-form')
const statusEl = document.getElementById('auth-status')

function getText() {
  return getMessages('pages/auth', getLocale())
}

function setStatus(text, isError = false) {
  if (!statusEl) return
  statusEl.textContent = text || ''
  statusEl.dataset.error = isError ? 'true' : 'false'
}

function translateValidationMessage(message) {
  const t = getText()
  const raw = String(message || '').trim()
  if (!raw) return t.requestFailed

  const map = {
    'Invalid email': t.invalidEmail,
    '邮箱格式不正确': t.invalidEmail,
    '用户名至少需要 3 个字符': t.usernameTooShort,
    'Username must be at least 3 characters': t.usernameTooShort,
    'String should have at least 8 characters': t.stringTooShort8,
    'String should have at least 3 characters': t.stringTooShort3,
    'Field required': t.fieldRequired,
  }
  return map[raw] || raw
}

function formatApiError(body, fallbackText) {
  if (!body) return fallbackText
  if (typeof body.detail === 'string' && body.detail.trim()) {
    return body.detail
  }
  if (Array.isArray(body.detail) && body.detail.length > 0) {
    const first = body.detail[0]
    if (typeof first?.msg === 'string' && first.msg.trim()) {
      return translateValidationMessage(first.msg)
    }
  }
  return fallbackText
}

function queryToken() {
  return new URLSearchParams(window.location.search).get('token') || ''
}

function applyLocalizedText() {
  const t = getText()
  const titleEl = document.getElementById('auth-title')
  const subtitleEl = document.getElementById('auth-subtitle')
  const identifierInput = document.getElementById('identifier')
  const emailInput = document.getElementById('email')
  const usernameInput = document.getElementById('username')
  const passwordInput = document.getElementById('password')
  const passwordConfirmInput = document.getElementById('password-confirm')
  const submitButton = form?.querySelector('button[type="submit"]')
  const authLinks = document.querySelectorAll('.auth-links a')
  document.querySelector('.auth-brand-text')?.replaceChildren(document.createTextNode(t.brandName || '天元计算'))

  if (mode === 'signup') {
    document.title = t.signupTitle
    if (titleEl) titleEl.textContent = t.signupHeading
    if (subtitleEl) subtitleEl.textContent = t.signupSubtitle
  } else if (mode === 'forgot-password') {
    document.title = t.forgotTitle
    if (titleEl) titleEl.textContent = t.forgotHeading
    if (subtitleEl) subtitleEl.textContent = t.forgotSubtitle
  } else if (mode === 'reset-password') {
    document.title = t.resetTitle
    if (titleEl) titleEl.textContent = t.resetHeading
    if (subtitleEl) subtitleEl.textContent = t.resetSubtitle
  } else {
    document.title = t.loginTitle
    if (titleEl) titleEl.textContent = t.loginHeading
    if (subtitleEl) subtitleEl.textContent = t.loginSubtitle
  }

  if (identifierInput) identifierInput.placeholder = t.identifierPlaceholder
  if (emailInput) emailInput.placeholder = t.emailPlaceholder
  if (usernameInput) usernameInput.placeholder = t.usernamePlaceholder
  if (passwordInput) passwordInput.placeholder = mode === 'signup' ? t.signupPasswordPlaceholder : mode === 'reset-password' ? t.resetPasswordPlaceholder : t.passwordPlaceholder
  if (passwordConfirmInput) passwordConfirmInput.placeholder = t.confirmPasswordPlaceholder

  if (submitButton) {
    if (mode === 'signup') submitButton.textContent = t.signupSubmit
    else if (mode === 'forgot-password') submitButton.textContent = t.forgotSubmit
    else if (mode === 'reset-password') submitButton.textContent = t.resetSubmit
    else submitButton.textContent = t.loginSubmit
  }

  if (mode === 'signup') {
    if (authLinks[0]) authLinks[0].textContent = t.signupLinkPrimary
    if (authLinks[1]) authLinks[1].textContent = t.signupLinkDocs
  } else if (mode === 'forgot-password' || mode === 'reset-password') {
    if (authLinks[0]) authLinks[0].textContent = t.backToLogin
    if (authLinks[1]) authLinks[1].textContent = t.backToWorkspace
  } else {
    if (authLinks[0]) authLinks[0].textContent = t.forgotLinkPrimary
    if (authLinks[1]) authLinks[1].textContent = t.loginLinkPrimary
    if (authLinks[2]) authLinks[2].textContent = t.loginLinkWorkspace
  }
}

async function submitLogin() {
  const t = getText()
  const identifier = document.getElementById('identifier').value.trim()
  const password = document.getElementById('password').value
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': localeHeaderValue(),
    },
    credentials: 'include',
    body: JSON.stringify({ identifier, password }),
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(formatApiError(body, t.loginFailed))
  window.location.href = resolveNextPath('/account')
}

async function submitSignup() {
  const t = getText()
  const email = document.getElementById('email').value.trim()
  const username = document.getElementById('username').value.trim()
  const password = document.getElementById('password').value
  const passwordConfirm = document.getElementById('password-confirm').value
  if (password !== passwordConfirm) throw new Error(t.passwordMismatch)

  const res = await fetch('/api/auth/register', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': localeHeaderValue(),
    },
    credentials: 'include',
    body: JSON.stringify({
      email,
      username: username || null,
      password,
    }),
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(formatApiError(body, t.signupFailed))
  window.location.href = resolveNextPath('/account')
}

async function submitForgotPassword() {
  const t = getText()
  const email = document.getElementById('email').value.trim()
  const res = await fetch('/api/auth/forgot-password', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': localeHeaderValue(),
    },
    body: JSON.stringify({ email }),
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(formatApiError(body, t.forgotFailed))
  setStatus(body.message || t.forgotSuccess)
}

async function submitResetPassword() {
  const t = getText()
  const token = queryToken()
  const password = document.getElementById('password').value
  const passwordConfirm = document.getElementById('password-confirm').value
  if (!token) throw new Error(t.resetMissingToken)
  if (password !== passwordConfirm) throw new Error(t.passwordMismatch)

  const res = await fetch('/api/auth/reset-password', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': localeHeaderValue(),
    },
    body: JSON.stringify({
      token,
      new_password: password,
    }),
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(formatApiError(body, t.resetFailed))
  setStatus(body.message || t.resetSuccess)
  window.setTimeout(() => {
    window.location.href = '/login'
  }, 1200)
}

form?.addEventListener('submit', async (event) => {
  event.preventDefault()
  const t = getText()
  setStatus('')
  try {
    if (mode === 'signup') await submitSignup()
    else if (mode === 'forgot-password') await submitForgotPassword()
    else if (mode === 'reset-password') await submitResetPassword()
    else await submitLogin()
  } catch (err) {
    setStatus(err.message || t.requestFailed, true)
  }
})

applyLocalizedText()
onLocaleChange(() => {
  setStatus('')
  applyLocalizedText()
})

reportPageView(window.location.pathname)

if (mode === 'login' || mode === 'signup') {
  fetchCurrentUser()
    .then((user) => {
      if (user) {
        window.location.href = resolveNextPath('/account')
      }
    })
    .catch(() => {
      // ignore auth preload failures on auth pages
    })
}
